import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "hooks";
import {
  selectBatteryGroup,
  selectBatteries,
} from "store/selectors";
import {
  fetchBatteryGroup,
  fetchBatteries,
} from "store/appSlice";
import MainLayout from "layouts/Main";
import { settingsTabs } from "views/Dashboard/Settings/tabs";
import {
  Box,
  Button,
  Typography,
  Modal,
  IconButton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import Table from "components/Table";
import AddBatteryModal from "./AddBatteryModal";
import { updateBatteryGroupBatteries } from "api/resources/batteryGroups";

const EditBatteryGroup = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const batteryGroup = useAppSelector(selectBatteryGroup);
  const allBatteries = useAppSelector(selectBatteries);
  const [openModal, setOpenModal] = useState(false);
  const [selectedBatteries, setSelectedBatteries] = useState<number[]>([]);

  useEffect(() => {
    if (id) {
      dispatch(fetchBatteryGroup(Number(id)));
      dispatch(fetchBatteries());
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (batteryGroup?.batteries) {
      setSelectedBatteries(batteryGroup.batteries.map((b) => b.id));
    }
  }, [batteryGroup]);

  const handleRemoveBattery = (batteryId: number) => {
    const updatedBatteryIds = selectedBatteries.filter(
      (id) => id !== batteryId,
    );
    setSelectedBatteries(updatedBatteryIds);

    updateBatteryGroupBatteries(Number(id), {
      batteryIds: updatedBatteryIds,
    })
      .then(() => {
        dispatch(fetchBatteryGroup(Number(id)));
        dispatch(fetchBatteries());
      })
      .catch((error) => {
        console.error("Error removing battery:", error);
      });
  };

  const handleAddBatteries = (batteryIds: number[]) => {
    const updatedBatteryIds = [...new Set([...selectedBatteries, ...batteryIds])];
    setSelectedBatteries(updatedBatteryIds);

    updateBatteryGroupBatteries(Number(id), {
      batteryIds: updatedBatteryIds,
    })
      .then(() => {
        dispatch(fetchBatteryGroup(Number(id)));
        dispatch(fetchBatteries());
        setOpenModal(false);
      })
      .catch((error) => {
        console.error("Error adding batteries:", error);
      });
  };

  const columns = [
    {
      accessorKey: "productionParkName",
      header: "Production Park",
    },
    {
      accessorKey: "externalId",
      header: "External ID",
      cell: ({ row }: { row: { original: any } }) => {
        return row.original.externalId || "-";
      },
    },
    {
      accessorKey: "capacity",
      header: "Capacity (kWh)",
    },
    {
      accessorKey: "inverterCapacity",
      header: "Inverter Capacity (kW)",
    },
    {
      accessorKey: "actions",
      header: "",
      cell: ({ row }: { row: { original: any } }) => {
        return (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => handleRemoveBattery(row.original.id)}
            >
              Remove
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <MainLayout
      tabs={settingsTabs.map((tab) =>
        tab.path === "BatteryGroups" ? { ...tab, selected: true } : tab,
      )}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton
          sx={{ mr: "0.6rem" }}
          size="small"
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography component="p">Back</Typography>
      </Box>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="h5">
            {batteryGroup?.name || "Battery Group"}
          </Typography>
          {batteryGroup?.externalId && (
            <Typography variant="body2" color="textSecondary">
              External ID: {batteryGroup.externalId}
            </Typography>
          )}
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setOpenModal(true)}
        >
          Add batteries
        </Button>
      </Box>
      <Table
        data={batteryGroup?.batteries || []}
        columns={columns}
        isFetching={!batteryGroup}
        pageCount={1}
        page={() => {}}
        searchLabel="Search batteries"
      />
      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <AddBatteryModal
          allBatteries={allBatteries || []}
          selectedBatteryIds={selectedBatteries}
          setOpenModal={setOpenModal}
          onAddBatteries={handleAddBatteries}
        />
      </Modal>
    </MainLayout>
  );
};

export default EditBatteryGroup;
